import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateAssignmentQueries, invalidateBidLinkQueries, invalidateStartAlignmentQueries } from '@/lib/queryClient';
import * as XLSX from 'xlsx';
import { projectsApi, usersApi, reportsApi, lookupsApi, type LookupOption } from '@/lib/apiClient';
import { fmtDate, toDateInputValue, todayInputValue, isDateInRange, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import {
  listPhaseSpans, earliestSpanStart, latestSpanEnd, remobOverride, PHASE_META,
  type PhaseSpan,
  type ProjectPhase, type RemobOverride,
} from '@/lib/schedule';
import ProjectsImportModal from './ProjectsImportModal';
import { BidMatchPanel, useBidMatches, resolveBidChoice, createThenLink, type BidChoice } from './BidLink';
import type { TravelBand } from '@/lib/travelBand';
import { Modal, Button, MultiSelect } from '@/components';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProjectStatus { id: number; label: string }
interface MobStatus     { id: number; label: string }
interface Division      { id: number; label: string }
interface UserRef       { id: string; displayName: string | null; email: string | null }

interface Project {
  id: number;
  jobNumber: string;
  title: string;
  client: string | null;
  /** Starred picker-assigned leader, server-derived from project_field_leaders. */
  primaryFieldLeader?: string | null;
  projectStatus: ProjectStatus | null;
  mobilizationStatus: MobStatus | null;
  division: Division | null;
  primaryPm: UserRef | null;
  secondaryPm: UserRef | null;
  estimator: UserRef | null;
  safetyManager: UserRef | null;
  projectEngineer: UserRef | null;
  currentStartDate: string | null;
  // Start date of the latest mobilization (highest sequence), if any.
  // A remob supersedes currentStartDate as the project's effective start.
  latestMobStart: string | null;
  originalStartDate: string | null;
  estimatedCompletion: string | null;
  // Latest mobilization end date; the API also sends effectiveCompletion =
  // max(estimatedCompletion, latestMobEnd) — a remob running past the original
  // date supersedes it for display, sorting, and health.
  latestMobEnd: string | null;
  effectiveCompletion: string | null;
  // Fabrication + installation rows, sent by the list only for jobs that have
  // them. Fab jobs show these instead of the single range — see listPhaseSpans.
  phases?: ProjectPhase[];
  estimatorHandoffDate: string | null;
  fieldLeaderHandoffDate: string | null;
  currentDuration: number | null;
  originalDuration: number | null;
  bidPeakCrew: number | null;
  bidWeeklyHours: number | null;
  contractValue: number | null;
  health: 'on-track' | 'at-risk' | 'off-track';
  delayNotes: string | null;
  estimatorNotes: string | null;
  pmNotes: string | null;
  jobAddress: string | null;
  siteContactName: string | null;
  siteContactPhone: string | null;
  siteContactEmail: string | null;
  travelJob: boolean;
  travelDistanceNote: string | null;
  /** The per-diem band the estimator priced. Already on the list response —
   *  `mapRow` is shared with the detail read — it was simply never declared
   *  here, so the export could not reach it. */
  travelBand: TravelBand | null;
  /** Subject to prevailing wage rates. Qualifies the travel-pay columns rather
   *  than being one of them — see EXPORT_COLUMNS. */
  isPrevailingWage: boolean;
  perDiem: boolean;
  perDiemDays: number | null;
  clinicName: string | null;
  clinicPhone: string | null;
  clinicAddress: string | null;
  jobFolderUrl: string | null;
  isArchived: boolean;
  // Set by the bids → PMT webhook (BID_CLOSED_WON). Combined with a
  // placeholder job_number prefix ("SYSTAC-" or "ClosedWon-") it drives
  // the renumber banner in the edit drawer.
  bidClosedWonAt: string | null;
  checklistDots: number[]; // valueId per checklist item, in item order
  checklistDotLabels: string[]; // item label per dot, aligned with checklistDots — drives the hover tooltip
}

// Project detail (checklist, mobilizations, notes) + the inline edit/delete UX
// now live on the project workspace's Overview (ProjectWorkspace.tsx +
// OverviewEdit.tsx). The portfolio list is navigate-only: a row click opens the
// workspace, where all editing happens.

type UserItem = { id: string; displayName: string | null; email: string | null; deletedAt: string | null };

// ─── Constants ────────────────────────────────────────────────────────────────

// valueId → dot colour
const DOT_COLORS: Record<number, string> = {
  1: '#027835', // Yes      — ew-green
  2: '#B8321C', // No       — ew-danger
  3: '#8A8A87', // N/A      — ew-n-400
  4: '#DB7A25', // Blocked  — ew-performance
  5: '#D9D9D7', // Not Set  — ew-n-200
};

// valueId → status label, used for the per-dot hover tooltip
const DOT_VALUE_LABELS: Record<number, string> = {
  1: 'Yes',
  2: 'No',
  3: 'N/A',
  4: 'Blocked',
  5: 'Not Set',
};

// Lifecycle-ordered, distinct hues so every status reads at a glance. The three
// that carry intuitive meaning keep their brand colors (Active green,
// Construction orange, Canceled red); the rest spread across clearly different
// colors. Each badge uses a single color (bg/border are tints of it).
const PROJECT_STATUS_COLORS: Record<string, string> = {
  'Pipeline':        '#2563EB', // blue — earliest / lead
  'Preconstruction': '#7C3AED', // violet — planning
  'Active':          '#027835', // green — go (brand)
  'Construction':    '#DB7A25', // orange — in progress (brand)
  'Schedule Only':   '#0F766E', // teal — parked / schedule-only
  'On Hold':         '#6B7280', // slate — paused
  'Complete':        '#363636', // near-black — done
  'Canceled':        '#B8321C', // red — stopped (brand)
};

const MOB_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  'ON TRACK':  { bg: '#E6F2EB', text: '#015A28' },
  'DELAYED':   { bg: '#FDECD9', text: '#9E4E10' },
  'OFF TRACK': { bg: '#FBE4DF', text: '#8F2414' },
  // Crew is on site — a progress state, not a health one, so it reads calm
  // rather than green-good (added 2026-07-28).
  'MOBILIZED': { bg: '#E7F0F8', text: '#15659E' },
};

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },     { id: 2, label: 'Construction' }, { id: 3, label: 'Complete' },
  { id: 4, label: 'Canceled' },   { id: 5, label: 'On Hold' },      { id: 6, label: 'Schedule Only' },
  { id: 7, label: 'Pipeline' },   { id: 8, label: 'Preconstruction' },
];
// Fallback only; the live list comes from /lookups/mobilization-statuses via
// useMobilizationOptions() so a value added in the database appears here too.
const MOB_OPTIONS_FALLBACK = [{ id: 1, label: 'ON TRACK' }, { id: 2, label: 'DELAYED' }, { id: 3, label: 'OFF TRACK' }];
// Division options come from the API (/lookups/divisions) so renames in the
// Admin tab propagate everywhere automatically. Fallback to the current seed
// labels if the request fails so the dropdown still renders.
const DIV_OPTIONS_FALLBACK: LookupOption[] = [
  { id: 1, label: 'Industrial' },
  { id: 2, label: 'Commercial' },
  { id: 3, label: 'GC' },
  { id: 4, label: 'Fab' },
];

function useMobilizationOptions(): LookupOption[] {
  const { data } = useQuery({
    queryKey: ['lookups', 'mobilization-statuses'],
    queryFn: lookupsApi.listMobilizationStatuses,
    staleTime: 5 * 60 * 1000,
  });
  return data ?? MOB_OPTIONS_FALLBACK;
}

function useDivisionOptions(): LookupOption[] {
  const { data } = useQuery({
    queryKey: ['lookups', 'divisions'],
    queryFn: lookupsApi.listDivisions,
    staleTime: 5 * 60 * 1000,
  });
  return data ?? DIV_OPTIONS_FALLBACK;
}

// ─── Small shared components ──────────────────────────────────────────────────

function ProjectStatusBadge({ label }: { label: string }) {
  const color = PROJECT_STATUS_COLORS[label] ?? '#6b7280';
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 'var(--ew-r-sm)', fontSize: 11, fontWeight: 600, background: color + '18', color, whiteSpace: 'nowrap', border: `1px solid ${color}33`, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {label}
    </span>
  );
}

function MobStatusBadge({ label }: { label: string }) {
  const c = MOB_STATUS_COLORS[label] ?? { bg: '#f3f4f6', text: '#6b7280' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 'var(--ew-r-sm)', fontSize: 11, fontWeight: 600, background: c.bg, color: c.text, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>
      {label}
    </span>
  );
}

function ChecklistDots({ dots, labels = [] }: { dots: number[]; labels?: string[] }) {
  if (!dots.length) return <span style={{ color: 'var(--gray)', fontSize: 11 }}>—</span>;
  const done = dots.filter(v => v === 1 || v === 3).length; // Yes or N/A
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: 120 }}>
        {dots.map((v, i) => {
          const item = labels[i];
          const status = DOT_VALUE_LABELS[v] ?? 'Not Set';
          const tip = item ? `${item}: ${status}` : status;
          return (
            <span key={i} title={tip} style={{ width: 8, height: 8, borderRadius: '50%', background: DOT_COLORS[v] ?? '#e5e7eb', display: 'inline-block', flexShrink: 0, cursor: 'default' }} />
          );
        })}
      </div>
      <span style={{ fontSize: 10, color: 'var(--gray)' }}>{done}/{dots.length}</span>
    </div>
  );
}

function WasDelayedBadge({ original, current }: { original: string | null; current: string | null }) {
  if (!original || !current) return null;
  const origMs = new Date(original).getTime();
  const curMs  = new Date(current).getTime();
  const days   = Math.round((curMs - origMs) / 86_400_000);
  if (days <= 0) return null;
  const label  = days >= 14 ? `+${Math.round(days / 7)}wks` : `+${days}d`;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, color: '#9E4E10', background: '#FDECD9', border: '1px solid rgba(219,122,37,0.3)', borderRadius: 'var(--ew-r-sm)', padding: '2px 7px', whiteSpace: 'nowrap', marginTop: 4, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      +{label} delayed
    </div>
  );
}

// True when the project's bid closed Won but the job# is still a placeholder.
// Single source of truth for the edit-drawer banner and the list-row pill.
function needsRenumber(p: Pick<Project, 'bidClosedWonAt' | 'jobNumber'>): boolean {
  return p.bidClosedWonAt != null
    && (p.jobNumber.startsWith('SYSTAC-') || p.jobNumber.startsWith('ClosedWon-'));
}

function RenumberPill() {
  return (
    <span
      title="Bid was won — replace this placeholder job number with a real one."
      style={{ display: 'inline-block', marginLeft: 8, padding: '2px 7px', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: '#7C4A03', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 'var(--ew-r-sm)', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
    >
      RENUMBER
    </span>
  );
}

// ─── Sort ─────────────────────────────────────────────────────────────────────

/**
 * Effective start date: the latest mobilization's start when the project has
 * been (re)mobilized, otherwise the planned current start. Mirrors the legacy
 * tracker, where a remob superseded Current Start in the table.
 *
 * ONE expression behind the column, the sort, the range filter and the export —
 * which is why the fab-phase case belongs here rather than only in the cell.
 * A fab row displays its earliest PHASE start, and `current_start_date` is
 * synced from `actualStart` alone (see earliestSpanStart), so reading the
 * project column here would let a row sort and filter as a date it isn't
 * showing. Non-fab rows get null from the span lookup and are unaffected.
 */
function effectiveStart(p: Project): string | null {
  return p.latestMobStart
    ?? earliestSpanStart(listPhaseSpans(p, p.phases))
    ?? p.currentStartDate;
}

/**
 * Effective completion: the mobilization end when one runs past the plan,
 * otherwise the job's last date.
 *
 * The companion to effectiveStart, and one expression behind the completion
 * sort and the export for the same reason. A fab row's last VISIBLE date is
 * either its latest phase end or, when a remobilization supersedes, the date on
 * the ↺ line — never `estimated_completion` on its own, which the CSV import
 * rewrites (applyImportUpdate) without touching the phases the cell is drawn
 * from. Non-fab rows get null spans and keep the project columns exactly.
 */
function effectiveCompletionOf(p: Project): string | null {
  const spans = listPhaseSpans(p, p.phases);
  if (spans) {
    // Whenever fabRemob fires its line is on screen, and its end is the job's
    // real last date.
    const remob = fabRemob(p, spans);
    return remob ? remob.end : latestSpanEnd(spans);
  }
  return p.effectiveCompletion ?? p.estimatedCompletion;
}

/**
 * A fab row's remobilization override, judged against the PHASE PLAN the cell
 * shows rather than the project columns.
 *
 * The project columns are the wrong yardstick here: the CSV import rewrites
 * `estimated_completion` without touching the phases, so a reimport that moves
 * it earlier than the plan makes an ordinary mobilization look like it runs
 * past a schedule it actually finishes inside of. Nothing on a fab row's dates
 * reads those columns any more.
 */
function fabRemob(p: Project, spans: PhaseSpan[]): RemobOverride | null {
  return remobOverride({
    latestMobStart: p.latestMobStart,
    latestMobEnd: p.latestMobEnd,
    planStart: earliestSpanStart(spans),
    planEnd: latestSpanEnd(spans),
  });
}

type SortKey ='jobNumber' | 'title' | 'client' | 'division' | 'status' | 'mobStatus' | 'pm' | 'startDate' | 'estCompletion';
type SortDir = 'asc' | 'desc';

const SORT_KEYS: readonly SortKey[] = [
  'jobNumber', 'title', 'client', 'division', 'status', 'mobStatus', 'pm', 'startDate', 'estCompletion',
];

/**
 * Sticky filters (#285).
 *
 * Filtering the list, opening a job and coming back used to land you on the
 * unfiltered list — the page unmounts on navigation and every filter lived in
 * plain component state, so a PM working through a filtered set had to rebuild
 * it after every job they looked at.
 *
 * sessionStorage rather than localStorage on purpose: a filter is a working
 * context for the task in hand, not a preference. It should survive clicking
 * into a job and back, and it should be gone tomorrow morning — coming back to
 * a list silently filtered by something you set last week is its own bug.
 * (`pmt_projects_scope` is a genuine preference and stays in localStorage.)
 */
const FILTERS_KEY = 'pmt_projects_filters';

interface PersistedFilters {
  search: string;
  statusIds: number[];
  divisionIds: number[];
  mobStatusIds: number[];
  dateFrom: string;
  dateTo: string;
  includeArchived: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
}

function readPersistedFilters(): Partial<PersistedFilters> {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<PersistedFilters>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {}; // storage unavailable, or a shape we no longer understand
  }
}

/** Ids come back from JSON as whatever was written; keep only real numbers so a
 *  hand-edited or stale blob can't poison a filter into matching nothing. */
const idSet = (v: unknown): Set<number> =>
  new Set(Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : []);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function sortProjects(rows: Project[], key: SortKey, dir: SortDir) {
  const f = dir === 'asc' ? 1 : -1;
  const s = (v: string | null | undefined) => v ?? '';
  return [...rows].sort((a, b) => {
    let av: string, bv: string;
    switch (key) {
      case 'jobNumber':  av = s(a.jobNumber);                 bv = s(b.jobNumber);                 break;
      case 'title':      av = s(a.title);                     bv = s(b.title);                     break;
      case 'client':     av = s(a.client);                    bv = s(b.client);                    break;
      case 'division':   av = s(a.division?.label);           bv = s(b.division?.label);           break;
      case 'status':     av = s(a.projectStatus?.label);      bv = s(b.projectStatus?.label);      break;
      case 'mobStatus':  av = s(a.mobilizationStatus?.label); bv = s(b.mobilizationStatus?.label); break;
      case 'pm':         av = s(a.primaryPm?.displayName);    bv = s(b.primaryPm?.displayName);    break;
      case 'startDate':  av = s(effectiveStart(a));           bv = s(effectiveStart(b));           break;
      // Effective completion: the latest mob end supersedes the stored date.
      case 'estCompletion': av = s(effectiveCompletionOf(a));            bv = s(effectiveCompletionOf(b));            break;
      default: return 0;
    }
    // Empty values sort as "earliest" — bubble to the top when ascending
    // and to the bottom when descending. localeCompare already treats
    // '' as less than any non-empty string, so no override needed.
    const primary = f * av.localeCompare(bv);
    if (primary !== 0 || key === 'startDate' || key === 'estCompletion') return primary;
    // Tie-break by effective start (soonest first) so grouping sorts — by PM,
    // status, division, etc. — come back date-ordered within each group
    // instead of arbitrarily (PM request).
    return s(effectiveStart(a)).localeCompare(s(effectiveStart(b)));
  });
}

// ─── Excel export ───────────────────────────────────────────────────────────

const yn = (v: boolean | null | undefined) => (v == null ? '' : v ? 'Yes' : 'No');
const d  = (iso: string | null | undefined) => toDateInputValue(iso) || ''; // YYYY-MM-DD

/**
 * Fuller-detail column set for the Excel export. Mirrors the on-screen columns
 * (Job #, Title, Client, Division, Status, Mob Status, PM, Planned Start,
 * Est. Compl.)
 * and adds the secondary roles, original/handoff dates, durations, travel/per
 * diem and checklist completion that PMs reach for in spreadsheet analysis.
 * Dates are emitted as YYYY-MM-DD so Excel sorts them chronologically; the
 * checklist count uses the same "Yes or N/A = done" rule as <ChecklistDots>.
 */
const EXPORT_COLUMNS: { header: string; value: (p: Project) => string | number | null }[] = [
  { header: 'Job #',            value: (p) => p.jobNumber },
  { header: 'Title',            value: (p) => p.title },
  { header: 'Client',           value: (p) => p.client ?? '' },
  { header: 'Division',         value: (p) => p.division?.label ?? '' },
  { header: 'Status',           value: (p) => p.projectStatus?.label ?? '' },
  { header: 'Mob Status',       value: (p) => p.mobilizationStatus?.label ?? '' },
  { header: 'Primary PM',       value: (p) => p.primaryPm?.displayName ?? '' },
  { header: 'Secondary PM',     value: (p) => p.secondaryPm?.displayName ?? '' },
  { header: 'Estimator',        value: (p) => p.estimator?.displayName ?? '' },
  { header: 'Project Engineer', value: (p) => p.projectEngineer?.displayName ?? '' },
  { header: 'Safety Manager',   value: (p) => p.safetyManager?.displayName ?? '' },
  { header: 'Field Leader',     value: (p) => p.primaryFieldLeader ?? '' },
  { header: 'Planned Start',    value: (p) => d(effectiveStart(p)) },
  { header: 'Current Start',    value: (p) => d(p.currentStartDate) },
  { header: 'Original Start',   value: (p) => d(p.originalStartDate) },
  { header: 'Est. Completion',  value: (p) => d(effectiveCompletionOf(p)) },
  { header: 'Estimator Handoff',value: (p) => d(p.estimatorHandoffDate) },
  { header: 'Field Handoff',    value: (p) => d(p.fieldLeaderHandoffDate) },
  { header: 'Current Duration', value: (p) => p.currentDuration ?? '' },
  { header: 'Original Duration',value: (p) => p.originalDuration ?? '' },
  { header: 'Bid Peak Crew',    value: (p) => p.bidPeakCrew ?? '' },
  { header: 'Bid Weekly Hours', value: (p) => p.bidWeeklyHours ?? '' },
  { header: 'Travel Job',       value: (p) => yn(p.travelJob) },
  // ⚠️ SITS BEFORE THE ENTITLEMENT COLUMNS BECAUSE IT QUALIFIES THEM. On a
  // prevailing-wage job the meals per diem and the whole hourly increase do not
  // apply (handbook item 6), so a reader who sees Travel Band and Per Diem
  // without this one draws the wrong conclusion about what is owed — and this
  // spreadsheet is what scheduling and payroll receive. Adding the flag without
  // exporting it would have made the export quietly misleading rather than
  // merely incomplete. (Codex, #533.)
  { header: 'Prevailing Wage',  value: (p) => yn(p.isPrevailingWage) },
  // The RAW band ('121-500'), not the '121-500 miles' the card shows. This is
  // the value stored in the column, so an export can be reconciled against the
  // Travel Acknowledgment tool character for character. The header carries the
  // meaning; the units would only get in the way of a filter.
  { header: 'Travel Band',      value: (p) => p.travelBand ?? '' },
  { header: 'Per Diem',         value: (p) => yn(p.perDiem) },
  // ⚠️ RENAMED FROM "Per Diem Days" (#504), which read as days OF per diem —
  // payroll's number, not this one. This is the weekly rotation, so the export
  // now matches the card and the activity feed rather than being the last place
  // the misnomer survived. It mattered more here than in the UI because this
  // spreadsheet is what scheduling and payroll actually receive.
  //
  // Anything keyed on the OLD header text — a saved formula, a pivot, a
  // template — reads this column as missing from the next export on. That cost
  // was accepted deliberately; see the PR.
  //
  // Stays the bare 5 / 7 rather than travelRotationLabel()'s sentence: the
  // header now carries the meaning, and a number sorts and filters in Excel.
  { header: 'Travel Rotation',  value: (p) => p.perDiemDays ?? '' },
  { header: 'Job Address',      value: (p) => p.jobAddress ?? '' },
  { header: 'Checklist Done',   value: (p) => (p.checklistDots.length ? `${p.checklistDots.filter((v) => v === 1 || v === 3).length}/${p.checklistDots.length}` : '') },
  { header: 'Checklist %',      value: (p) => (p.checklistDots.length ? Math.round((p.checklistDots.filter((v) => v === 1 || v === 3).length / p.checklistDots.length) * 100) : '') },
  { header: 'Archived',         value: (p) => yn(p.isArchived) },
];

/** Build and trigger download of an .xlsx for the given (already-sorted) rows. */
function exportProjectsToXlsx(rows: Project[]) {
  const header = EXPORT_COLUMNS.map((c) => c.header);
  const body = rows.map((p) => EXPORT_COLUMNS.map((c) => c.value(p)));
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws['!cols'] = EXPORT_COLUMNS.map((c) => ({ wch: Math.max(c.header.length + 2, 14) }));
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: EXPORT_COLUMNS.length - 1 } }) };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Projects');
  // The EXPORTER'S calendar date. ⚠️ Not `toDateInputValue(new Date().toISOString())`,
  // which is the UTC date: an Eastern user exporting after 8pm is already on the next
  // UTC day and files a workbook stamped TOMORROW. `todayInputValue` builds from local
  // components for exactly this reason, and says so in its own docblock.
  const stamp = todayInputValue();
  XLSX.writeFile(wb, `projects-export-${stamp}.xlsx`);
}

/** One clickable sort target inside a merged header (e.g. "Status / Mob"). */
function SortSpan({ label, k, current, dir, onSort }: {
  label: string; k: SortKey; current: SortKey; dir: SortDir; onSort: (k: SortKey) => void;
}) {
  const active = current === k;
  return (
    <span
      onClick={() => onSort(k)}
      style={{ cursor: 'pointer', userSelect: 'none', color: active ? 'var(--ew)' : undefined }}
    >
      {label} <span style={{ opacity: active ? 1 : 0.3 }}>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </span>
  );
}

/** Merged two-in-one column header — each half sorts its own key. Keeps the
 *  table narrow enough to fit without horizontal scrolling (PM feedback:
 *  fewer columns beats tighter ones). */
function ThDual({ a, b, current, dir, onSort, className }: {
  a: { label: string; k: SortKey }; b: { label: string; k: SortKey };
  current: SortKey; dir: SortDir; onSort: (k: SortKey) => void; className?: string;
}) {
  return (
    <th className={className} style={{
      padding: '8px 12px', textAlign: 'left', whiteSpace: 'nowrap',
      fontWeight: 600, fontSize: 12, color: 'var(--gray)',
      borderBottom: '2px solid var(--border)', background: 'var(--surface)',
      position: 'sticky', top: 0, zIndex: 5,
    }}>
      <SortSpan label={a.label} k={a.k} current={current} dir={dir} onSort={onSort} />
      <span style={{ opacity: 0.35 }}> / </span>
      <SortSpan label={b.label} k={b.k} current={current} dir={dir} onSort={onSort} />
    </th>
  );
}

/**
 * Says which side of the range the remobilization moved, and off what.
 *
 * Quotes the PHASE dates — what the comparison was made against, and what the
 * reader can see on the lines just above. Naming the project columns here would
 * cite a figure that isn't on the row.
 */
function remobTitle(r: RemobOverride, spans: PhaseSpan[]): string {
  const planStart = earliestSpanStart(spans);
  const planEnd = latestSpanEnd(spans);
  const parts: string[] = [];
  if (r.startSuperseded) parts.push(`supersedes the planned start (${fmtDate(planStart)})`);
  if (r.endSuperseded) {
    parts.push(planEnd
      ? `runs past the planned completion (${fmtDate(planEnd)})`
      : 'sets the completion (none was planned)');
  }
  return `Remobilization — ${parts.join(', and ')}. The phase rows above are the plan.`;
}

/**
 * The Planned Start / Est. Compl. cell.
 *
 * Normally two lines: start on top, completion beneath with a → prefix, and ↺
 * marking a date superseded by a remobilization.
 *
 * A fab job gets one line per phase instead. It runs a shop phase and a field
 * phase, and the row's single range is only their outer hull — on prod it is
 * exactly min(phase start) → max(phase end) on every fab job, so it flattens
 * both the wait between shop and site and the overlap when they run together
 * (#334). Same two lines, so the row height doesn't move.
 *
 * A remobilization becomes its own line there rather than sitting on a date: it
 * belongs to the JOB, and pinning it to one phase would be asserting which
 * phase was remobilized, which we don't know.
 *
 * ⚠️ That line carries the effective DATES, not just the word "Remobilized".
 * The phase rows are the plan; a remob is the job running somewhere else, and
 * a reader scanning for the latest date on this job has to be able to see it
 * without hovering. See remobOverride for why both sides are judged separately.
 */
export function ScheduleCell({ p }: { p: Project }) {
  const spans = listPhaseSpans(p, p.phases);

  if (spans) {
    const remob = fabRemob(p, spans);
    return (
      <>
        {spans.map((s) => (
          <div key={s.phase} style={{ marginTop: 1 }}
            title={`${PHASE_META[s.phase].label} — planned start; actual end where entered`}>
            <span className="pp-phase-tag">{s.short}</span>
            {fmtDate(s.start)}
            <span style={{ color: 'var(--ew-n-400)' }}> → </span>
            {fmtDate(s.end)}
          </div>
        ))}
        {remob && (
          <div style={{ marginTop: 1 }} title={remobTitle(remob, spans)}>
            <span className="pp-phase-tag" style={{ color: 'var(--ew)' }}>↺ Job</span>
            {fmtDate(remob.start)}
            <span style={{ color: 'var(--ew-n-400)' }}> → </span>
            {fmtDate(remob.end)}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div>
        {p.latestMobStart && (
          <span title={`Remobilization — supersedes planned start (${fmtDate(p.currentStartDate)})`}
            style={{ color: 'var(--ew)', fontWeight: 700, marginRight: 3 }}>↺</span>
        )}
        {fmtDate(effectiveStart(p))}
      </div>
      <div style={{ marginTop: 1 }}>
        <span style={{ color: 'var(--ew-n-400)' }} title="Estimated completion">→ </span>
        {p.effectiveCompletion && p.estimatedCompletion
          && p.effectiveCompletion.slice(0, 10) > p.estimatedCompletion.slice(0, 10) && (
          <span title={`Remobilization runs past the planned completion (${fmtDate(p.estimatedCompletion)}) — showing the mob end date`}
            style={{ color: 'var(--ew)', fontWeight: 700, marginRight: 3 }}>↺</span>
        )}
        {fmtDate(p.effectiveCompletion ?? p.estimatedCompletion)}
      </div>
    </>
  );
}

function Th({ label, sortKey, current, dir, onSort, className }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir; onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = current === sortKey;
  return (
    <th className={className} onClick={() => onSort(sortKey)} style={{
      padding: '8px 12px', textAlign: 'left', whiteSpace: 'nowrap',
      fontWeight: 600, fontSize: 12, color: active ? 'var(--ew)' : 'var(--gray)',
      cursor: 'pointer', userSelect: 'none', borderBottom: '2px solid var(--border)',
      background: 'var(--surface)',
      // Headers pin while the table scrolls (PM request) — the table wrapper
      // owns vertical scroll so sticky has a container to stick to.
      position: 'sticky', top: 0, zIndex: 5,
    }}>
      {label} <span style={{ opacity: active ? 1 : 0.3 }}>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

// ─── KPI Bar ──────────────────────────────────────────────────────────────────

const kpiCss = `
  .pp-kpi-strip { display: grid; gap: var(--ew-s-4); grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); margin-bottom: var(--ew-s-3); }
  .pp-kpi { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: var(--ew-s-4); position: relative; overflow: hidden; display: flex; flex-direction: column; gap: var(--ew-s-1); }
  .pp-kpi-bar { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
  .pp-kpi-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .pp-kpi-value { font: var(--ew-fw-semibold) 28px/1.05 var(--ew-font); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; color: var(--ew-dark); }
  .pp-kpi-sub { font: var(--ew-fw-medium) 12px/1.2 var(--ew-font); color: var(--ew-n-500); }
`;

function KpiBar({ projects, kpi, kpiLoading }: {
  projects: Project[];
  kpi: { overallScore: number; scoreBand: string; checklistCompletePct: number; activeProjectCount: number } | undefined;
  kpiLoading: boolean;
}) {
  const counts = useMemo(() => {
    let onTrack = 0, atRisk = 0, offTrack = 0;
    for (const p of projects) {
      if (p.health === 'at-risk') atRisk++;
      else if (p.health === 'off-track') offTrack++;
      else onTrack++;
    }
    return { total: projects.length, onTrack, atRisk, offTrack };
  }, [projects]);

  const scoreColor = kpi
    ? (kpi.scoreBand === 'strong' ? 'var(--ew-green)' : kpi.scoreBand === 'good' ? 'var(--ew-performance)' : 'var(--ew-danger)')
    : 'var(--ew-n-400)';

  const tiles: { label: string; value: React.ReactNode; sub?: React.ReactNode; strip: string }[] = [
    { label: 'Total jobs', value: counts.total,    strip: 'var(--ew-n-300)' },
    { label: 'On track',   value: counts.onTrack,  strip: 'var(--ew-green)' },
    { label: 'At risk',    value: counts.atRisk,   strip: 'var(--ew-safety)' },
    { label: 'Off track',  value: counts.offTrack, strip: 'var(--ew-danger)' },
    { label: 'Readiness',  value: kpiLoading || !kpi ? '—' : `${kpi.overallScore}%`,
      sub: kpi ? `${kpi.activeProjectCount} starting ≤ 2 wks` : undefined, strip: scoreColor },
  ];

  return (
    <>
      <style>{kpiCss}</style>
      <div className="pp-kpi-strip">
        {tiles.map((t) => (
          <div key={t.label} className="pp-kpi">
            <span className="pp-kpi-bar" style={{ background: t.strip }} />
            <span className="pp-kpi-label">{t.label}</span>
            <span className="pp-kpi-value">{t.value}</span>
            {t.sub ? <span className="pp-kpi-sub">{t.sub}</span> : null}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Inline Detail Row ────────────────────────────────────────────────────────

const HEALTH_META: Record<'on-track' | 'at-risk' | 'off-track', { label: string; color: string }> = {
  'on-track':  { label: 'On track',  color: 'var(--ew-green)' },
  'at-risk':   { label: 'At risk',   color: 'var(--ew-safety)' },
  'off-track': { label: 'Off track', color: 'var(--ew-danger)' },
};

/** Compact project-health pill (colored dot + label) for the portfolio table.
 *  Color is never the only signal — the text label carries the same meaning. */
function HealthIndicator({ health }: { health: 'on-track' | 'at-risk' | 'off-track' }) {
  const m = HEALTH_META[health] ?? HEALTH_META['on-track'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', color: 'var(--ew-n-700)', whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '999px', background: m.color, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// ─── New Project Modal ─────────────────────────────────────────────────────────

// Manual project creation is Admin-only (2026-10-05). Every job arrives from Bid Tracker;
// an Admin can still add one by hand as the exception. The server enforces the same rule
// (`POST /projects` needs `admin.access`), so hiding the button here is not the control.
// Only Job # and Title are required; everything else can be filled in here or later on
// the project workspace.
function NewProjectModal({ users, onClose, onCreated }: {
  users: UserItem[];
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const qc = useQueryClient();
  const divisionOptions = useDivisionOptions();
  const mobOptions = useMobilizationOptions();
  const [form, setForm] = useState<Record<string, string>>({
    jobNumber: '', title: '', client: '', divisionId: '', projectStatusId: '1',
    mobilizationStatusId: '', primaryPmId: '', secondaryPmId: '', projectEngineerId: '',
    estimatorId: '', safetyManagerId: '',
    originalStartDate: '', currentStartDate: '', estimatedCompletion: '',
  });
  const [err, setErr] = useState<string | null>(null);
  // Open bid placeholders that look like this job. If any do, the PM has to say whether
  // it is one of them: a job typed in by hand for a bid that is already here is the
  // duplicate this panel exists to prevent (see BidLink.tsx).
  // The answer is remembered with the bids it was given against, so "No, a different job"
  // does not silently cover a bid that only appeared as the PM kept typing.
  const [bidAnswer, setBidAnswer] = useState<{ choice: BidChoice; against: number[] }>({ choice: null, against: [] });
  const f = (k: string) => form[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));
  const { matches: bidMatches, isPending: bidMatchesPending } = useBidMatches(f('title'), f('client'));
  const effectiveBidChoice = resolveBidChoice(bidMatches, bidAnswer.choice, bidAnswer.against);

  const createMut = useMutation({
    mutationFn: () => {
      const ne = (v: string) => (v.trim() === '' ? undefined : v.trim());
      return createThenLink(() => projectsApi.create({
        jobNumber: form.jobNumber.trim(),
        title: form.title.trim(),
        client: ne(form.client),
        divisionId: form.divisionId ? Number(form.divisionId) : undefined,
        projectStatusId: form.projectStatusId ? Number(form.projectStatusId) : undefined,
        mobilizationStatusId: form.mobilizationStatusId ? Number(form.mobilizationStatusId) : undefined,
        primaryPmId: ne(form.primaryPmId), secondaryPmId: ne(form.secondaryPmId),
        projectEngineerId: ne(form.projectEngineerId), estimatorId: ne(form.estimatorId),
        safetyManagerId: ne(form.safetyManagerId),
        originalStartDate: ne(form.originalStartDate), currentStartDate: ne(form.currentStartDate),
        estimatedCompletion: ne(form.estimatedCompletion),
      }) as Promise<{ id: number }>, effectiveBidChoice, projectsApi.linkBid)
        // The placeholder that was actually linked, or null if none was, read from the choice
        // this request was made with (a const of this render, so typing while it is in flight
        // cannot change it).
        .then((done) => ({
          ...done,
          linkedPlaceholderId: typeof effectiveBidChoice === 'number' && done.linkError === null ? effectiveBidChoice : null,
        }));
    },
    onSuccess: ({ project: proj, linkError, linkedPlaceholderId }) => {
      // The job exists either way. If only the link failed, say so and carry on to the
      // job, where "Link to a bid" retries it, rather than leaving the PM on a form
      // that would create a second copy.
      if (linkError) {
        window.alert(`Job ${form.jobNumber.trim()} was created, but linking it to the bid failed: ${linkError}\n\nOpen the job and use "Link to a bid" on its Overview.`);
      }
      qc.invalidateQueries({ queryKey: ['kpi'] });
      if (linkedPlaceholderId != null) {
        // A link archives the placeholder: it must stop being offered as a match, its own
        // detail is stale, and the schedule and worklists that read it move too. Includes
        // the assignment caches.
        invalidateBidLinkQueries(qc, proj.id, linkedPlaceholderId);
      } else {
        // A refused link usually means the placeholder was just taken or archived, so the
        // matches are re-read rather than offered again.
        if (linkError) qc.invalidateQueries({ queryKey: ['bid-placeholders'] });
        // The form has a Primary PM select, so you can create a job with yourself
        // on it — which is somebody's FIRST job often enough to matter. Covers
        // `projects` too.
        invalidateAssignmentQueries(qc, proj.id);
      }
      onCreated(proj.id);
    },
    onError: (e: any) => setErr(e?.message ?? 'Failed to create project'),
  });

  const canSubmit = !!form.jobNumber.trim() && !!form.title.trim() && !bidMatchesPending && effectiveBidChoice !== null && !createMut.isPending;
  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw: React.CSSProperties = { display: 'flex', flexDirection: 'column' };
  const g2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' };
  // A NEW project can only ever be assigned to someone still here, so this
  // picker filters removed people outright — there is no existing value to
  // preserve, unlike the Team selects on an existing job.
  const activeUsers = users.filter(u => !u.deletedAt);
  const userOpts = (k: string) => (
    <select style={inp} value={f(k)} onChange={set(k)}>
      <option value="">— None —</option>
      {activeUsers.map(u => <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>)}
    </select>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New Project"
      maxWidth={680}
      closeOnBackdropClick={false}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => { setErr(null); createMut.mutate(); }} disabled={!canSubmit} isLoading={createMut.isPending}>
          Create Project
        </Button>
      </>}
    >
      {err && <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{err}</div>}
      <div style={g2}>
        <div style={fw}><label style={lbl}>Job # *</label><input style={inp} value={f('jobNumber')} onChange={set('jobNumber')} placeholder="e.g. 10567" autoFocus /></div>
        <div style={fw}><label style={lbl}>Title *</label><input style={inp} value={f('title')} onChange={set('title')} /></div>
        <div style={fw}><label style={lbl}>Client</label><input style={inp} value={f('client')} onChange={set('client')} /></div>
        <div style={fw}><label style={lbl}>Division</label>
          <select style={inp} value={f('divisionId')} onChange={set('divisionId')}>
            <option value="">— None —</option>
            {divisionOptions.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
        <div style={fw}><label style={lbl}>Project Status</label>
          <select style={inp} value={f('projectStatusId')} onChange={set('projectStatusId')}>
            {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div style={fw}><label style={lbl}>Mobilization Status</label>
          <select style={inp} value={f('mobilizationStatusId')} onChange={set('mobilizationStatusId')}>
            <option value="">— None —</option>
            {mobOptions.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div style={fw}><label style={lbl}>Primary PM</label>{userOpts('primaryPmId')}</div>
        <div style={fw}><label style={lbl}>Secondary PM</label>{userOpts('secondaryPmId')}</div>
        <div style={fw}><label style={lbl}>Project Engineer</label>{userOpts('projectEngineerId')}</div>
        <div style={fw}><label style={lbl}>Estimator</label>{userOpts('estimatorId')}</div>
        <div style={fw}><label style={lbl}>Safety Manager</label>{userOpts('safetyManagerId')}</div>
        <div style={fw}><label style={lbl}>Original Start</label><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inp} value={f('originalStartDate')} onChange={set('originalStartDate')} /></div>
        <div style={fw}><label style={lbl}>Current Start</label><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inp} value={f('currentStartDate')} onChange={set('currentStartDate')} /></div>
        <div style={fw}><label style={lbl}>Est. Completion</label><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inp} value={f('estimatedCompletion')} onChange={set('estimatedCompletion')} /></div>
      </div>
      <BidMatchPanel
        matches={bidMatches}
        choice={effectiveBidChoice}
        onChoose={(c) => setBidAnswer({ choice: c, against: bidMatches.map((m) => m.id) })}
      />
      <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 12 }}>
        After creating, the project opens in its workspace so you can add checklist items, mobilizations, and notes.
      </div>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

/**
 * Horizontal scroll container that surfaces a scroll affordance. The projects
 * table is wider than a phone/tablet viewport even after responsive column
 * hiding, and the native horizontal scrollbar sits at the bottom of a tall
 * table — well below the fold — so on a small screen the rows just looked
 * clipped, as if data were missing or the page were broken.
 *
 * This wraps the scroll area and paints a soft edge gradient on whichever side
 * has more content to reveal. The fade is purely a cue (pointer-events: none),
 * spans the full table height so it's visible at any vertical scroll position,
 * and animates in/out as you reach each end. CSS-only scroll-shadows don't work
 * here because the opaque table rows would paint over them — hence the overlay.
 */
function HScrollArea({ children, viewportRef }: {
  children: React.ReactNode;
  /** Optional handle on the scrolling element, for callers that need to move
   *  it — the toolbar resets it to the top when a filter reshapes the list. */
  viewportRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = viewportRef ?? ownRef;
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 1,
      right: Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [update]);

  const fadeBase: React.CSSProperties = {
    position: 'absolute', top: 0, bottom: 0, width: 32, pointerEvents: 'none',
    transition: 'opacity 150ms var(--ew-ease, ease)',
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Owns BOTH scroll axes: an overflow-x-only ancestor would break the
          sticky column headers (position:sticky pins to the nearest scroll
          container). Capped height keeps the header row on screen while long
          project lists scroll under it. Scrollbars are thinned because the
          internal vertical bar consumes width — at full-width viewports the
          table sits near the edge, and a fat scrollbar tipped it into
          needless horizontal scrolling. */}
      <style>{`
        .pp-scroll-area { scrollbar-width: thin; scrollbar-color: var(--ew-n-300) transparent; }
        .pp-scroll-area::-webkit-scrollbar { width: 8px; height: 8px; }
        .pp-scroll-area::-webkit-scrollbar-thumb { background: var(--ew-n-300); border-radius: 4px; }
        .pp-scroll-area::-webkit-scrollbar-track { background: transparent; }
      `}</style>
      <div ref={ref} onScroll={update} className="pp-scroll-area" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
        {children}
      </div>
      <div aria-hidden style={{
        ...fadeBase, left: 0, opacity: edges.left ? 1 : 0,
        background: 'linear-gradient(to right, rgba(54,54,54,0.16), rgba(54,54,54,0))',
      }} />
      <div aria-hidden style={{
        ...fadeBase, right: 0, opacity: edges.right ? 1 : 0,
        background: 'linear-gradient(to left, rgba(54,54,54,0.16), rgba(54,54,54,0))',
      }} />
    </div>
  );
}

export default function ProjectsPage() {
  const mobOptions = useMobilizationOptions();
  // Restored once, on mount — the filters the user left the page with (#285).
  const saved = useRef(readPersistedFilters()).current;
  const [search, setSearch]           = useState(() => str(saved.search));
  // All three filter dropdowns are multi-selects (PM request: e.g.
  // Preconstruction + Construction viewed together). Filtered client-side —
  // the list the user can see is small, and it avoids widening the API's
  // single-value filter params.
  const [statusIds, setStatusIds]         = useState<Set<number>>(() => idSet(saved.statusIds));
  const [divisionIds, setDivisionIds]     = useState<Set<number>>(() => idSet(saved.divisionIds));
  const [mobStatusIds, setMobStatusIds]   = useState<Set<number>>(() => idSet(saved.mobStatusIds));
  // Start-date range filter (client-side, on effective Start — the same value
  // the Start column shows and the default sort uses). Empty = no bound.
  const [dateFrom, setDateFrom]           = useState(() => str(saved.dateFrom));
  const [dateTo, setDateTo]               = useState(() => str(saved.dateTo));
  const toggleIn = (set: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const [scope, setScope]             = useState<'mine' | 'all'>(
    // Remember the user's last My Projects/All choice across visits.
    () => { try { return localStorage.getItem('pmt_projects_scope') === 'all' ? 'all' : 'mine'; } catch { return 'mine'; } },
  );
  const divisionOptions               = useDivisionOptions();
  const [includeArchived, setIncludeArchived] = useState(() => saved.includeArchived === true);
  const [sortKey, setSortKey] = useState<SortKey>(
    () => (saved.sortKey && SORT_KEYS.includes(saved.sortKey) ? saved.sortKey : 'startDate'),
  );
  const [sortDir, setSortDir] = useState<SortDir>(() => (saved.sortDir === 'desc' ? 'desc' : 'asc'));

  // Write the working filter set back on every change, so opening a job and
  // returning lands on the list the user actually left.
  useEffect(() => {
    const blob: PersistedFilters = {
      search,
      statusIds:    [...statusIds],
      divisionIds:  [...divisionIds],
      mobStatusIds: [...mobStatusIds],
      dateFrom, dateTo, includeArchived, sortKey, sortDir,
    };
    try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(blob)); } catch { /* storage unavailable */ }
  }, [search, statusIds, divisionIds, mobStatusIds, dateFrom, dateTo, includeArchived, sortKey, sortDir]);

  // Sort and the My/All scope are deliberately NOT counted here: they order or
  // frame the list rather than hide rows from it, and offering to "clear" a
  // sort that always has some value reads as broken.
  const anyFilterActive =
    search.trim() !== '' || statusIds.size > 0 || divisionIds.size > 0
    || mobStatusIds.size > 0 || dateFrom !== '' || dateTo !== '' || includeArchived;

  const clearFilters = () => {
    setSearch('');
    setStatusIds(new Set());
    setDivisionIds(new Set());
    setMobStatusIds(new Set());
    setDateFrom('');
    setDateTo('');
    setIncludeArchived(false);
    scrollListToTop();
  };

  // The table scrolls inside its own capped-height viewport. Narrowing the list
  // leaves scrollTop where it was, and the browser clamps it to the shorter
  // content — so "Today", which usually cuts the list down hard, dumped the user
  // at the BOTTOM of the results it had just filtered to, with today's jobs off
  // screen above them (#287). Sort is start-date ascending, so the top is
  // exactly where they want to be.
  const listViewportRef = useRef<HTMLDivElement>(null);
  const scrollListToTop = () => listViewportRef.current?.scrollTo({ top: 0 });

  const navigate = useNavigate();
  // CSV import modal (Admin-only)
  const [showImport, setShowImport] = useState(false);
  // Manual "New Project" modal (project editors / Admin)
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();

  // Determine Admin role for visibility of the Import button.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  const isAdmin = (me?.roles ?? []).some((r) => r.toLowerCase().replace(/\s+/g, '') === 'admin');
  // Adding one by hand is Admin-only, like the CSV import. Deliberately NOT gated on
  // `projects.edit`: a project editor can change a job but not create one.
  const canCreate = isAdmin;
  // Excel export is an assignable permission (Admin › Roles), like every other
  // capability. It used to be a hardcoded admin/executive/projectadmin role
  // check — the only role-name gate in the app — which meant widening access
  // needed a code change. The 2026-07-25 migration backfilled the grant to
  // whichever roles passed that old check, so nobody lost access.
  const canExport = isAdmin || (me?.permissions ?? []).includes('projects.export');
  // Whether to offer the "My / All" toggle: only users who can actually see
  // beyond their own assignments (view-all permission, Admin, or a division grant).
  const canSeeBeyondMine = !!me && (me.canViewAll || (me.divisions?.length ?? 0) > 0);
  // Keep scope valid if a user without broader access somehow lands on 'all'.
  const effectiveScope: 'mine' | 'all' = canSeeBeyondMine ? scope : 'mine';
  // Persist the toggle so it sticks across visits — e.g. users with no assigned
  // projects can stay on All instead of re-toggling every time they open Projects.
  useEffect(() => {
    try { localStorage.setItem('pmt_projects_scope', scope); } catch { /* storage unavailable */ }
  }, [scope]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const params = useMemo(() => ({
    scope: effectiveScope,
    ...(search        ? { search }                          : {}),
    ...(includeArchived ? { includeArchived: true }         : {}),
  }), [effectiveScope, search, includeArchived]);

  const { data, isLoading, error } = useQuery<Project[]>({
    queryKey: ['projects', params],
    queryFn: () => projectsApi.list(params),
    staleTime: 30_000,
  });

  const { data: kpi, isLoading: kpiLoading } = useQuery({
    queryKey: ['kpi', effectiveScope],
    queryFn: () => reportsApi.kpi(effectiveScope),
    staleTime: 60_000,
  });

  // `users` powers the New Project modal's PM/engineer/estimator/safety selects.
  const { data: users = [] } = useQuery<UserItem[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 300_000,
  });

  // Multi-select filters apply client-side; an empty selection = all.
  const filtered = useMemo(
    () => (data ?? []).filter((p) => {
      if (statusIds.size    > 0 && !(p.projectStatus != null && statusIds.has(p.projectStatus.id)))       return false;
      if (divisionIds.size  > 0 && !(p.division != null && divisionIds.has(p.division.id)))               return false;
      if (mobStatusIds.size > 0 && !(p.mobilizationStatus != null && mobStatusIds.has(p.mobilizationStatus.id))) return false;
      // Start-date range on the effective Start (same value shown/sorted in
      // the Start column). A project with no start date drops out once a bound
      // is set. See isDateInRange for the exact semantics.
      if (!isDateInRange(effectiveStart(p), dateFrom, dateTo)) return false;
      return true;
    }),
    [data, statusIds, divisionIds, mobStatusIds, dateFrom, dateTo],
  );
  const sorted = useMemo(() => sortProjects(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  // Export exactly what's on screen: the same rows, in the same order, honoring
  // the search box, every filter, the My/All scope and Show archived.
  //
  // This used to refetch with scope:'all' + includeArchived:true and export the
  // whole portfolio regardless of the filter bar — so filtering to 12 projects
  // and hitting Export handed you all 137, including archived ones and projects
  // outside your scope. What-you-see-is-what-you-get is the predictable
  // behavior, and nothing is lost: clearing the filters still exports
  // everything. `sorted` is already filtered + sorted, so no refetch is needed.
  const handleExport = useCallback(() => {
    try {
      exportProjectsToXlsx(sorted);
    } catch (e) {
      alert(`Export failed: ${(e as Error).message}`);
    }
  }, [sorted]);

  const selectStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
    fontSize: 13, background: '#fff', color: 'var(--text)', cursor: 'pointer', minWidth: 140,
  };

  const COL_COUNT = 9; // Job# | Title | Client | Division | Status+Mob | PM | Start→Compl | Progress | Actions

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>Projects</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {canExport && (
            <button
              onClick={handleExport}
              disabled={sorted.length === 0}
              className="btn btn-secondary btn-sm"
              title={
                sorted.length === 0
                  ? 'Nothing to export — no projects match your filters'
                  : `Export these ${sorted.length} project${sorted.length !== 1 ? 's' : ''} to Excel, in the current sort order. Clear the filters to export everything.`
              }
            >
              ⬇ Export Excel
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowImport(true)}
              className="btn btn-secondary btn-sm"
              title="Import projects from a SharePoint CSV (Admin only)"
            >
              ↑ Import CSV
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => setShowNew(true)}
              className="btn btn-primary btn-sm"
              title="Manually add a new project (Admin only — jobs normally arrive from Bid Tracker)"
            >
              + New Project
            </button>
          )}
          <span style={{ fontSize: 13, color: 'var(--gray)' }}>{isLoading ? 'Loading…' : `${sorted.length} project${sorted.length !== 1 ? 's' : ''}`}</span>
        </div>
      </div>

      {/* KPI bar */}
      {!isLoading && sorted.length > 0 && <KpiBar projects={sorted} kpi={kpi} kpiLoading={kpiLoading} />}

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, padding: '12px 16px', background: '#fff', borderRadius: 'var(--ew-r-md)', border: '1px solid var(--border)' }}>
        {canSeeBeyondMine && (
          <div role="group" aria-label="Project visibility" style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', overflow: 'hidden' }}>
            {(['mine', 'all'] as const).map(s => (
              <button
                key={s}
                onClick={() => setScope(s)}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: effectiveScope === s ? 'var(--ew)' : '#fff',
                  color: effectiveScope === s ? '#fff' : 'var(--gray)',
                }}
              >
                {s === 'mine' ? 'My Projects' : 'All'}
              </button>
            ))}
          </div>
        )}
        <input type="search" placeholder="Search job #, title, client…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, minWidth: 240, flexGrow: 1, backgroundImage: 'none' }} />
        {/* Multi-select dropdowns (PM request) — check several options to see
            them together, e.g. Preconstruction + Construction. Empty = all. */}
        <MultiSelect label="Status" options={STATUS_OPTIONS} selected={statusIds}
          onToggle={toggleIn(setStatusIds)} onClear={() => setStatusIds(new Set())} />
        <MultiSelect label="Division" options={divisionOptions} selected={divisionIds}
          onToggle={toggleIn(setDivisionIds)} onClear={() => setDivisionIds(new Set())} />
        <MultiSelect label="Mob Status" options={mobOptions} selected={mobStatusIds}
          onToggle={toggleIn(setMobStatusIds)} onClear={() => setMobStatusIds(new Set())} />
        {/* Start-date range + Today jump (PM request). Two bare date inputs in
            one bordered shell read as a single range field. "Today" sets From
            to today and clears To → projects starting today or later. */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '0 4px 0 10px', background: '#fff' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray)', whiteSpace: 'nowrap' }}>Start</span>
          <input type="date" aria-label="Start date from" min={DATE_INPUT_MIN} max={dateTo || DATE_INPUT_MAX}
            value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)', background: 'transparent', padding: '5px 2px', width: 128 }} />
          <span style={{ color: 'var(--ew-n-400)' }}>–</span>
          <input type="date" aria-label="Start date to" min={dateFrom || DATE_INPUT_MIN} max={DATE_INPUT_MAX}
            value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)', background: 'transparent', padding: '5px 2px', width: 128 }} />
          <button type="button" onClick={() => { setDateFrom(todayInputValue()); setDateTo(''); scrollListToTop(); }}
            title="Show projects starting today or later"
            style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-sm)', background: 'var(--surface)', color: 'var(--ew)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Today
          </button>
          {(dateFrom || dateTo) && (
            <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }}
              title="Clear date range" aria-label="Clear start-date range"
              style={{ fontSize: 15, lineHeight: 1, padding: '2px 6px', border: 'none', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}>
              ×
            </button>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: 'var(--gray)' }}>
          <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
        {/* Filters now survive navigating into a job and back (#285), so a list
            can arrive already narrowed by something set several jobs ago. One
            click has to undo all of it — otherwise the only way back to the
            full list is clearing four controls by hand. Shown only when there
            is something to clear. */}
        {anyFilterActive && (
          <button type="button" onClick={clearFilters}
            title="Clear the search box and every filter"
            style={{ fontSize: 12, fontWeight: 600, padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-sm)', background: 'var(--surface)', color: 'var(--gray)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear filters
          </button>
        )}
      </div>

      {error && <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', marginBottom: 16, fontSize: 13 }}>{(error as Error).message}</div>}

      {/* Responsive column-hiding for narrow viewports. PMs use this on phones
          during job-site walks — we hide secondary columns below tablet width
          and trim down to identity-only columns at phone width. */}
      <style>{`
        /* Fab/Install tag on the dates cell. Fixed width so the two dates below
           it line up into columns rather than stepping in and out. */
        /* 48px, not 42 — "INSTALL" measures 45 and would bleed into its own
           gutter at 42, which is only invisible because nothing clips it. */
        .pp-phase-tag {
          display: inline-block; width: 48px; margin-right: 4px;
          font: var(--ew-fw-semibold) 10px/1.6 var(--ew-font);
          letter-spacing: var(--ew-ls-caption); text-transform: uppercase;
          color: var(--ew-n-500);
        }
        @media (max-width: 768px) {
          .pp-col-hide-md { display: none !important; }
          .pp-field-sub   { display: none; }
        }
        @media (max-width: 480px) {
          .pp-col-hide-sm { display: none !important; }
        }
      `}</style>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 'var(--ew-r-md)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <HScrollArea viewportRef={listViewportRef}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <Th label="Job #"      sortKey="jobNumber"  current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Title"      sortKey="title"      current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Client"     sortKey="client"     current={sortKey} dir={sortDir} onSort={handleSort} className="pp-col-hide-sm" />
                <Th label="Division"   sortKey="division"   current={sortKey} dir={sortDir} onSort={handleSort} className="pp-col-hide-md" />
                <ThDual a={{ label: 'Status', k: 'status' }} b={{ label: 'Mob', k: 'mobStatus' }}
                  current={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="PM"         sortKey="pm"         current={sortKey} dir={sortDir} onSort={handleSort} />
                <ThDual a={{ label: 'Planned Start', k: 'startDate' }} b={{ label: 'Compl.', k: 'estCompletion' }}
                  current={sortKey} dir={sortDir} onSort={handleSort} className="pp-col-hide-md" />
                <th className="pp-col-hide-md" style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--gray)', borderBottom: '2px solid var(--border)', background: 'var(--surface)', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 5 }}>
                  Progress &amp; Checklist
                </th>
                <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--border)', background: 'var(--surface)', width: 108, position: 'sticky', top: 0, zIndex: 5 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading projects…</td></tr>
              )}
              {!isLoading && sorted.length === 0 && (
                <tr><td colSpan={COL_COUNT} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>No projects match your filters.</td></tr>
              )}
              {sorted.map((p, i) => {
                const rowBg = i % 2 === 0 ? '#fff' : 'var(--surface)';
                return (
                    <tr key={p.id}
                      onClick={() => navigate(`/projects/${p.id}`)}
                      style={{ background: rowBg, borderBottom: '1px solid var(--border)', opacity: p.isArchived ? 0.65 : 1, cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--ew-dim)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                      <td style={{ padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--ew)' }}>
                        {p.jobNumber}
                        {needsRenumber(p) && <RenumberPill />}
                      </td>
                      <td style={{ padding: '9px 12px', maxWidth: 260 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                        {p.primaryFieldLeader && <div className="pp-field-sub" style={{ fontSize: 11, color: 'var(--gray)', marginTop: 1 }}>Field: {p.primaryFieldLeader}</div>}
                        {(p.travelJob || p.perDiem) && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                            {p.travelJob && <span style={{ fontSize: 10, background: '#E1EEF7', color: '#0D4C7C', borderRadius: 'var(--ew-r-sm)', padding: '2px 6px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>TRAVEL</span>}
                            {p.perDiem && <span style={{ fontSize: 10, background: '#FDECD9', color: '#9E4E10', borderRadius: 'var(--ew-r-sm)', padding: '2px 6px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>PER DIEM</span>}
                          </div>
                        )}
                      </td>
                      <td className="pp-col-hide-sm" style={{ padding: '9px 12px', color: p.client ? 'var(--text)' : 'var(--gray)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.client ?? '—'}</td>
                      <td className="pp-col-hide-md" style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: 'var(--gray)' }}>{p.division?.label ?? '—'}</td>
                      {/* Status + Mob merged into one stacked cell (fits the
                          table to the viewport; a missing mob just shows
                          nothing instead of a dash). */}
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                          {p.projectStatus ? <ProjectStatusBadge label={p.projectStatus.label} /> : <span style={{ color: 'var(--gray)' }}>—</span>}
                          {p.mobilizationStatus && <MobStatusBadge label={p.mobilizationStatus.label} />}
                          {p.mobilizationStatus?.label === 'ON TRACK' && (
                            <WasDelayedBadge original={p.originalStartDate} current={p.currentStartDate} />
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        {p.primaryPm?.displayName ?? <span style={{ color: 'var(--gray)' }}>—</span>}
                        {p.secondaryPm && <div className="pp-field-sub" style={{ fontSize: 11, color: 'var(--gray)', marginTop: 1 }} title="Secondary PM">2nd: {p.secondaryPm.displayName}</div>}
                      </td>
                      <td className="pp-col-hide-md" style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: 'var(--gray)', fontSize: 12 }}>
                        <ScheduleCell p={p} />
                      </td>
                      <td className="pp-col-hide-md" style={{ padding: '9px 12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                          <HealthIndicator health={p.health} />
                          <ChecklistDots dots={p.checklistDots} labels={p.checklistDotLabels} />
                        </div>
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          {p.jobFolderUrl ? (
                            <a href={p.jobFolderUrl} target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              title="Open job folder"
                              aria-label="Open job folder"
                              style={{ fontSize: 11, padding: '3px 9px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--text)', cursor: 'pointer', textDecoration: 'none' }}>
                              📁
                            </a>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}
                              title="No folder linked — open the project to add one"
                              aria-label="Open project to add job folder URL"
                              className="btn btn-secondary btn-sm"
                              style={{ borderStyle: 'dashed', opacity: 0.55 }}>
                              📁
                            </button>
                          )}
                          <button onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}
                            className="btn btn-secondary btn-sm">
                            Open
                          </button>
                        </div>
                      </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </HScrollArea>
      </div>

      {!isLoading && sorted.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray)', textAlign: 'right' }}>
          {sorted.length} project{sorted.length !== 1 ? 's' : ''}{(search || statusIds.size > 0 || mobStatusIds.size > 0 || divisionIds.size > 0 || dateFrom || dateTo) ? ' (filtered)' : ''}
        </div>
      )}

      {/* Admin-only CSV import modal */}
      {showImport && isAdmin && (
        <ProjectsImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            void qc.invalidateQueries({ queryKey: ['report-kpi'] });
            // An import can put the importer on a job, or take them off one.
            void invalidateAssignmentQueries(qc);
            // It also overwrites current_start_date on a PHASE-LESS job
            // (setPhaseOwned leaves phased jobs alone), which is the plan half of
            // the start-mismatch flag -- so the crew grid's copy goes stale (#385).
            void invalidateStartAlignmentQueries(qc);
          }}
        />
      )}

      {showNew && canCreate && (
        <NewProjectModal
          users={users}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); navigate(`/projects/${id}`); }}
        />
      )}
    </div>
  );
}
